import React, {useCallback, useEffect, useMemo, useRef} from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../theme/ThemeProvider";
import BaseChart from "./BaseChart";

/**
 * LineChart component that renders multiple line series with gradients and tooltips using Canvas for hopefully incredible performance
 *
 * @param {Object} props
 * @param {Array} props.data - The dataset with x and y values for each series
 * @param {Array} props.series - Array of series configurations (color, name, etc.)
 * @param {Object} props.settings - Chart settings and appearance options
 */
const LineChart = ({data = [], series = [], settings = {}, ...rest}) => {
  const chartSettings = useMemo(() => ({...settings}), [settings]);

  /**
   * For any poor soul trying to decypher this component in the future, a couple notes for you.
   * It's made using HTML5 Canvas, which is a bit of a pain to work with, but it should be fast.
   * Take a look at https://www.w3schools.com/html/html5_canvas.asp for a good reference
   * Send @Chloe Hill a note if you need clarification.
   */
  const LineChartRenderer = ({
    canvasRef,
    containerRef,
    chartSettings,
    hovering,
    setTooltipData,
    setTooltipPosition,
  }) => {
    const activePointIndexRef = useRef(null);
    const {theme} = useTheme();
    const {processedSeries, yDomain} = useMemo(() => {
      if (!data.length || !series.length) {
        return {processedSeries: [], yDomain: [0, 100]};
      }

      let minValue = Infinity;
      let maxValue = -Infinity;

      const processedSeries = series.map((s) => {
        const seriesData = data.map((d) => {
          const value = d[s.dataKey] || 0;
          minValue = Math.min(minValue, value);
          maxValue = Math.max(maxValue, value);

          return {
            x: d.x,
            value,
          };
        });

        return {
          ...s,
          data: seriesData,
        };
      });

      minValue = Math.max(0, minValue * 0.8);
      maxValue = maxValue * 1.2;

      return {processedSeries, yDomain: [minValue, maxValue]};
    }, [data, series]);

    const drawChart = useCallback(() => {
      if (!canvasRef.current || !containerRef.current || data.length === 0)
        return;

      const containerWidth = containerRef.current.clientWidth || 0;
      const containerHeight = chartSettings.height || 0;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", {alpha: true});

      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;

      ctx.scale(dpr, dpr);
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const padding = chartSettings.padding;
      const chartWidth = containerWidth - padding.left - padding.right;
      const chartHeight = containerHeight - padding.top - padding.bottom;

      const xScale = (index) =>
        padding.left + (index / (data.length - 1)) * chartWidth;
      const yScale = (value) =>
        padding.top +
        chartHeight -
        ((value - yDomain[0]) / (yDomain[1] - yDomain[0])) * chartHeight;

      const yTicks = chartSettings.axis.y.tickCount;
      ctx.strokeStyle = chartSettings.grid.color || theme.colors.gray200;
      ctx.lineWidth = 1;
      ctx.setLineDash(chartSettings.grid.dashArray);

      const yLabels = [];

      for (let i = 0; i < yTicks; i++) {
        const y = padding.top + (i / (yTicks - 1)) * chartHeight;
        const value =
          yDomain[1] - (i / (yTicks - 1)) * (yDomain[1] - yDomain[0]);

        if (chartSettings.grid.horizontal) {
          ctx.beginPath();
          ctx.moveTo(padding.left, y);
          ctx.lineTo(containerWidth - padding.right, y);
          ctx.stroke();
        }

        yLabels.push({
          text: Math.round(value).toString(),
          x: padding.left - 10,
          y: y + 4,
        });
      }

      ctx.strokeStyle = chartSettings.axis.x.tickColor || theme.colors.gray300;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(padding.left, padding.top + chartHeight);
      ctx.lineTo(containerWidth - padding.right, padding.top + chartHeight);
      ctx.stroke();

      if (chartSettings.grid.vertical) {
        ctx.strokeStyle = chartSettings.grid.color || theme.colors.gray200;
        ctx.lineWidth = 1;
        ctx.setLineDash(chartSettings.grid.dashArray);

        const xTickCount = chartSettings.axis.x.tickCount;
        const interval = Math.max(1, Math.ceil(data.length / xTickCount));

        for (let i = 0; i < data.length; i += interval) {
          const x = xScale(i);

          ctx.beginPath();
          ctx.moveTo(x, padding.top);
          ctx.lineTo(x, padding.top + chartHeight);
          ctx.stroke();
        }
      }

      const xLabels = [];
      const labelInterval = Math.max(
        1,
        Math.ceil(data.length / chartSettings.axis.x.tickCount),
      );

      for (let i = 0; i < data.length; i += labelInterval) {
        if (i < data.length) {
          xLabels.push({
            text: data[i].x,
            x: xScale(i),
            y: containerHeight - padding.bottom / 3,
          });
        }
      }

      if ((data.length - 1) % labelInterval !== 0) {
        xLabels.push({
          text: data[data.length - 1].x,
          x: xScale(data.length - 1),
          y: containerHeight - padding.bottom / 3,
        });
      }

      processedSeries.forEach((s) => {
        const points = [];

        for (let i = 0; i < data.length; i++) {
          points.push({
            x: xScale(i),
            y: yScale(data[i][s.dataKey] || 0),
          });
        }

        ctx.beginPath();
        for (let i = 0; i < points.length; i++) {
          if (i === 0) {
            ctx.moveTo(points[i].x, points[i].y);
          } else {
            ctx.lineTo(points[i].x, points[i].y);
          }
        }

        ctx.lineTo(points[points.length - 1].x, padding.top + chartHeight);
        ctx.lineTo(points[0].x, padding.top + chartHeight);
        ctx.closePath();

        const gradient = ctx.createLinearGradient(
          0,
          padding.top,
          0,
          padding.top + chartHeight,
        );
        gradient.addColorStop(0, `${s.color}66`);
        gradient.addColorStop(1, `${s.color}0D`);

        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        for (let i = 0; i < points.length; i++) {
          if (i === 0) {
            ctx.moveTo(points[i].x, points[i].y);
          } else {
            ctx.lineTo(points[i].x, points[i].y);
          }
        }

        ctx.strokeStyle = s.color;
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.stroke();

        if (
          activePointIndexRef.current !== null &&
          hovering &&
          activePointIndexRef.current < points.length
        ) {
          const point = points[activePointIndexRef.current];

          ctx.beginPath();
          ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = theme.colors.white;
          ctx.strokeStyle = s.color;
          ctx.lineWidth = 2;
          ctx.setLineDash([]);
          ctx.fill();
          ctx.stroke();
        }
      });

      if (activePointIndexRef.current !== null && hovering) {
        const activeX = xScale(activePointIndexRef.current);

        ctx.beginPath();
        ctx.moveTo(activeX, padding.top);
        ctx.lineTo(activeX, padding.top + chartHeight);
        ctx.strokeStyle = theme.colors.gray400;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
      }

      ctx.fillStyle = theme.colors.textSecondary;
      ctx.font = `${chartSettings.axis.y.labelFontSize}px sans-serif`;

      ctx.textAlign = "right";
      yLabels.forEach((label) => {
        ctx.fillText(label.text, label.x, label.y);
      });

      ctx.textAlign = "center";
      xLabels.forEach((label) => {
        ctx.fillText(label.text, label.x, label.y);
      });
    }, [
      data,
      processedSeries,
      yDomain,
      chartSettings,
      hovering,
      activePointIndexRef.current,
    ]);

    const handleMouseMove = useCallback(
      (e) => {
        if (!containerRef.current || !canvasRef.current || data.length === 0)
          return;

        try {
          const rect = canvasRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;

          const containerWidth = containerRef.current.clientWidth || 0;
          if (containerWidth <= 0) return;

          const chartWidth =
            containerWidth -
            chartSettings.padding.left -
            chartSettings.padding.right;

          const xPercent = (x - chartSettings.padding.left) / chartWidth;
          if (xPercent < 0 || xPercent > 1) return;

          const index = Math.min(
            Math.max(0, Math.round(xPercent * (data.length - 1))),
            data.length - 1,
          );

          if (index !== activePointIndexRef.current) {
            activePointIndexRef.current = index;

            const xPos =
              chartSettings.padding.left +
              (index / (data.length - 1)) * chartWidth;

            const tooltipContent = {
              items: series.map((s) => ({
                label: s.name,
                value: data[index][s.dataKey]?.toLocaleString() || "0",
                color: s.color,
              })),
            };
            setTooltipData(tooltipContent);
            setTooltipPosition({x: xPos, y: chartSettings.padding.top + 50});
          }
        } catch (err) {
          console.error("Error in chart mouse handling:", err);
        }
      },
      [data, series, chartSettings, setTooltipData, setTooltipPosition],
    );

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const boundMouseMove = (e) => handleMouseMove(e);
      canvas.addEventListener("mousemove", boundMouseMove);

      return () => {
        canvas.removeEventListener("mousemove", boundMouseMove);
      };
    }, [handleMouseMove]);

    useEffect(() => {
      if (canvasRef.current && containerRef.current && data.length > 0) {
        drawChart();
      }
    }, [drawChart]);

    useEffect(() => {
      const handleResize = () => {
        if (canvasRef.current && containerRef.current && data.length > 0) {
          drawChart();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [drawChart, data.length]);

    return null;
  };

  return (
    <BaseChart
      data={data}
      series={series}
      settings={chartSettings}
      renderer={LineChartRenderer}
      {...rest}
    />
  );
};

LineChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  settings: PropTypes.object,
};

export default LineChart;
